<template>
  <v-card outlined class="pb-6 px-0 mb-4" flat :loading="isFetchPending">
    <ErrorAlert
      v-if="error != null"
      :error="error"
      @clearErrors="clearErrors()"
      class="mb-0 rounded"
    />
    <div class="d-flex flex-column flex-grow-1">
      <v-list>
        <v-list-item three-line class="py-6">
          <template v-slot:prepend>
            <v-avatar color="grey">
              <span class="white--text">{{ currentUser?.getInitials() }}</span>
            </v-avatar>
          </template>
          <template v-slot:title>
            <v-form v-model="commentValid" ref="form">
              <v-textarea
                v-model="comment.text"
                :rules="[requiredValidator]"
                placeholder="Add a comment..."
                :error-messages="error && (error.fieldErrors['text'] as string[])"
                :loading="loading"
                rows="1"
                label="Comment"
                auto-grow
                outlined
                hide-details="auto"
                @input="clearErrors"
              >
                <template #append-inner>
                  <div class="v-input__icon v-input__icon--append justify-end">
                    <v-icon
                      color="primary"
                      @click.stop="addComment()"
                      :disabled="!canSaveComment && !commentValid"
                      size="22"
                    >
                      mdi-send
                    </v-icon>
                  </div>
                </template>
              </v-textarea>
            </v-form>
          </template>
        </v-list-item>

        <v-divider inset />
        <template v-if="comments">
          <div
            class="title font-weight-regular grey--text text-center my-3"
            v-if="comments.length == 0"
          >
            No comments for this form submission
          </div>
          <template v-else v-for="(comment, commentIndex) in comments" :key="commentIndex">
            <ChitCommentListItem :chit="chit" :chitComment="comment" />
            <v-divider
              inset
              :key="`${commentIndex}_divider`"
              v-if="commentIndex < comments.length - 1"
            />
          </template>
        </template>
      </v-list>
    </div>
  </v-card>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { ChitComment, type IChit, type IChitComment, type IChitCommentData } from '@/models/chit'
import { requiredValidator } from '@/utils/validators'
import type { VForm } from 'vuetify/components'

import ChitCommentListItem from './ChitCommentListItem.vue'
import ErrorAlert from '../common/ErrorAlert.vue'
import { useCreateChitComment, useFetchChitComments } from '@/composables/chit'
import type { ISystemError } from '@/models/error'
import { useAuthStore } from '@/stores'
import { storeToRefs } from 'pinia'

interface Props {
  chit: IChit
}

const props = defineProps<Props>()

const commentValid = ref(false)
const comment = ref<IChitCommentData>({} as IChitCommentData)
const form = ref<VForm | null>(null)

const authProvider = useAuthStore()
const { getUser: currentUser } = storeToRefs(authProvider)
const {
  comments,
  isPending: isFetchPending,
  error: fetchError
} = useFetchChitComments(props.chit.chit_number!)

const {
  isPending: isCreatePending,
  error: createError,
  reset: createReset,
  mutate: createChitComment
} = useCreateChitComment()

const loading = computed(() => isCreatePending.value || isFetchPending.value)
const canSaveComment = computed(() => comment.value.text && !loading.value)
const localError = ref<ISystemError | null>(null)

const error = computed({
  get: () => {
    return localError.value || createError.value || fetchError.value
  },
  set: (value) => {
    localError.value = value
  }
})

function clearErrors() {
  createReset()
  form.value?.resetValidation()
  error.value = null
}

function addComment() {
  const valid = form.value?.validate()
  if (valid) {
    let chitCommentInstance: IChitComment | null = null

    try {
      chitCommentInstance = new ChitComment(comment.value)
    } catch (e: any) {
      error.value = e
    }

    if (chitCommentInstance) {
      createChitComment(
        { data: chitCommentInstance, chitNumber: props.chit.chit_number! },
        {
          onSuccess: () => {
            comment.value = {} as IChitCommentData
            commentValid.value = false
            form.value?.resetValidation()
          }
        }
      )
    }
  }
}
</script>

<style></style>
